import React, { useEffect, useState } from 'react';
import * as Sentry from "@sentry/react";
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import {
  Bookmark, Share2, MessageCircle, Copy, MapPin, Download,
  Calendar, DollarSign, Clock, Star, CheckCircle, Check, Flag, Trash2,
  Users, Heart, ChevronRight, ArrowLeft,
  Wallet, Utensils, Plane, Bus, Ticket, Building2, Map, Sparkles, ThumbsUp, ThumbsDown, Lightbulb
} from 'lucide-react';
import { toast } from 'react-toastify';
import { CommunityError } from "../../errors/sentryErrors";
import { trackEvent } from "../../utils/analytics";
import { jsPDF } from 'jspdf';
import html2pdf from 'html2pdf.js';
import { getAuthToken, useAuth } from '../../contexts/AuthContext';
import { getCommunityPostDetail, copyPost, deleteReview } from '../../services/api';
import MediaLightbox from '../../components/community/MediaLightbox';
import LikeButton from '../../components/community/LikeButton';
import SaveButton from '../../components/community/SaveButton';
import ShareBottomSheet from '../../components/community/ShareBottomSheet';
import CommentList from '../../components/community/CommentList';
import ReportModal from '../../components/community/ReportModal';
import FollowButton from '../../components/community/profile/FollowButton';
import BadgeStrip from '../../components/community/badges/BadgeStrip';
import DayWiseItinerarySection from '../../components/itinerary/DayWiseItinerarySection';
import ItineraryMap from '../../components/itinerary/ItineraryMap';
import HotelInformationSection from '../../components/itinerary/HotelInformationSection';
import { generateItineraryPDF } from '../../components/itinerary';
import { PAGE_CONTAINER } from '../../components/layout/pageWidth';

// One colour per budget category, in the order the categories are ranked (largest first): the icon tile, the bar and the share pill match.
const BUDGET_TONES = [
  { tile: 'bg-teal-50 text-teal-600', bar: 'bg-teal-500', pill: 'bg-teal-50 text-teal-700' },
  { tile: 'bg-amber-50 text-amber-500', bar: 'bg-amber-400', pill: 'bg-amber-50 text-amber-700' },
  { tile: 'bg-sky-50 text-sky-500', bar: 'bg-sky-500', pill: 'bg-sky-50 text-sky-700' },
  { tile: 'bg-violet-50 text-violet-500', bar: 'bg-violet-500', pill: 'bg-violet-50 text-violet-700' },
  { tile: 'bg-rose-50 text-rose-500', bar: 'bg-rose-400', pill: 'bg-rose-50 text-rose-700' },
];

const PostDetail = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === 'true';
  const navigate = useNavigate();
  
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCopying, setIsCopying] = useState(false);
  const { isBanned } = useAuth();
  
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [showShare, setShowShare] = useState(false);
  const [reportingPostId, setReportingPostId] = useState(null);
  
  // Which section the tab bar is highlighting. The tabs scroll the page rather
  // than swapping its contents, so this is presentation only.
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedDays, setExpandedDays] = useState({ 0: true });
  const [hotelsExpanded, setHotelsExpanded] = useState(true);
  
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const toggleDay = (index) => {
    setExpandedDays(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleDownloadPDF = async () => {
    if (!post?.trip?.itinerary) {
      toast.error("Itinerary data not found.");
      return;
    }
    const toastId = toast.loading("Generating PDF... Please wait.");
    try {
      const itinerary = post.trip.itinerary;
      const tripData = post.trip;
      const finalCostSummary = itinerary.costSummary;
      
      await generateItineraryPDF(
        itinerary,
        tripData,
        finalCostSummary,
        {},
        null
      );
      trackEvent("post_pdf_downloaded", { post_id: post.id, destination: post.trip?.destination });
      toast.dismiss(toastId);
      toast.success("PDF generated successfully!");
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "community");
        scope.setTag("app", "web");
        scope.setTag("error_source", "browser");
        scope.setContext("post", { slug });
        Sentry.captureException(new CommunityError(error instanceof Error ? error.message : String(error)));
      });
      console.error("Failed to generate PDF:", error);
      toast.dismiss(toastId);
      toast.error("Failed to download itinerary. Please try again.");
    }
  };

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const data = await getCommunityPostDetail(slug, isPreview);
        if (!data) {
          setError("This post is no longer available.");
        } else {
          const normalizedPost = {
            id: data.post.id,
            slug: data.post.slug,
            status: data.post.status,
            trip: data.trip,
            review: data.review,
            user: data.user,
            media: (data.media || []).filter(m => m.status !== 'rejected'),
            coverPhoto: (data.media || []).find(m => m.category === 'cover' && m.status !== 'rejected') || (data.media || []).find(m => m.status !== 'rejected'),
            counters: {
              likeCount: data.post.likeCount || 0,
              saveCount: data.post.saveCount || 0,
              commentCount: data.post.commentCount || 0,
              copyCount: data.post.copyCount || 0,
              viewCount: data.post.viewCount || 0,
            },
            viewerState: data.viewerState,
            isLikedByViewer: data.viewerState?.liked,
            isSavedByViewer: data.viewerState?.saved,
          };
          setPost(normalizedPost);
          trackEvent("post_detail_viewed", {
            post_id: normalizedPost.id,
            slug: normalizedPost.slug,
            is_preview: isPreview,
            destination: data.trip?.destination,
          });
        }
      } catch (err) {
        Sentry.withScope((scope) => {
          scope.setTag("feature", "community");
          scope.setTag("app", "web");
          scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
          scope.setContext("post", { slug, post_id: post?.id });
          Sentry.captureException(new CommunityError(err instanceof Error ? err.message : String(err)));
        });
        setError("Error loading post. Please try again later.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug, isPreview]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{error || "Post not found"}</h2>
        <Link to="/community" className="text-blue-600 hover:underline">Return to Community</Link>
      </div>
    );
  }

  const { trip, review, user, media, coverPhoto, counters, viewerState, status } = post;
  const startDate = new Date(trip.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const endDate = new Date(trip.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  // Derived from the trip's own dates rather than a hardcoded 8, which showed
  // "8 Days / 7 Nights" on every trip that lacked a duration field.
  const tripDays = (() => {
    const from = new Date(trip.startDate);
    const to = new Date(trip.endDate);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return trip.duration || 1;
    return Math.max(1, Math.round((to - from) / 86400000) + 1);
  })();

  const travellerCount = (trip.adults || 1) + (trip.children || 0);
  const travellerLabel =
    travellerCount === 1 ? "Solo" : `${travellerCount} Travellers`;

  // Budget, in the same order of preference the dashboard card uses: what the
  // traveller set, else what the itinerary costed out. The label follows the
  // source so an estimate is never presented as the traveller's own budget.
  const budgetInfo = (() => {
    const currency =
      trip.currency || trip.itinerary?.tripSummary?.budget?.currency || "INR";

    const compact = (amount) =>
      amount >= 100000
        ? `${currency} ${(amount / 100000).toFixed(amount % 100000 === 0 ? 0 : 1)}L`
        : amount >= 1000
          ? `${currency} ${(amount / 1000).toFixed(amount % 1000 === 0 ? 0 : 1)}k`
          : `${currency} ${amount.toLocaleString()}`;

    if (Number(trip.budget) > 0) {
      return { label: "Budget", value: compact(Number(trip.budget)) };
    }

    const estimated =
      trip.itinerary?.costSummary?.estimatedTotal ??
      trip.itinerary?.tripSummary?.budget?.amount;

    // The itinerary sometimes stores this pre-formatted ("INR 85,000").
    if (typeof estimated === "string" && estimated.trim()) {
      return { label: "Est. cost", value: estimated.trim() };
    }
    if (Number(estimated) > 0) {
      return { label: "Est. cost", value: compact(Number(estimated)) };
    }

    return { label: "Budget", value: "Not shared" };
  })();

  // Cost breakdown for the budget section. Amounts are stored pre-formatted
  // ("₹25,135"), so the digits are pulled out only to size the proportion bars —
  // the string the traveller sees is always the one the itinerary stored.
  const costSummary = trip.itinerary?.costSummary;
  const COST_CATEGORIES = [
    { key: "accommodation", label: "Accommodation", Icon: Building2 },
    { key: "transport", label: "Transport", Icon: Plane },
    { key: "food", label: "Food & dining", Icon: Utensils },
    { key: "activities", label: "Activities", Icon: Ticket },
    { key: "localTransport", label: "Local transport", Icon: Bus },
  ];
  const toAmount = (value) => {
    if (typeof value === "number") return value;
    const digits = String(value ?? "").replace(/[^\d.]/g, "");
    const n = parseFloat(digits);
    return Number.isFinite(n) ? n : 0;
  };
  const costRows = COST_CATEGORIES
    .map((cat) => ({ ...cat, display: costSummary?.breakdown?.[cat.key], amount: toAmount(costSummary?.breakdown?.[cat.key]) }))
    .filter((row) => row.display && row.amount > 0)
    .sort((a, b) => b.amount - a.amount);
  const costTotal = costRows.reduce((sum, row) => sum + row.amount, 0);
  const hasBudgetSection = costRows.length > 0 || !!costSummary?.estimatedTotal;

  // HotelInformationSection builds its stay suggestions from citiesBreakdown
  // (see getCitiesFromItinerary), so that — not an accommodations array — is what
  // decides whether the section has anything to show.
  const hasHotelSection = Boolean(
    trip?.itinerary?.tripSummary?.citiesBreakdown?.some(
      (city) => city?.type !== "departure" && parseInt(city?.nights ?? 1, 10) > 0,
    ),
  );

  const openLightbox = (index) => {
    const item = media[index];
    trackEvent("post_gallery_opened", {
      post_id: post.id,
      media_index: index,
      media_kind: item?.kind || 'image',
      category: item?.category || 'other',
    });
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const handleLightboxNav = (direction) => {
    const newIndex = direction === 'prev'
      ? (lightboxIndex > 0 ? lightboxIndex - 1 : media.length - 1)
      : (lightboxIndex < media.length - 1 ? lightboxIndex + 1 : 0);
    trackEvent("media_lightbox_navigated", { post_id: post.id, direction, media_index: newIndex });
    setLightboxIndex(newIndex);
  };

  const handleCopy = async () => {
    Sentry.addBreadcrumb({
      category: "user-action",
      message: "Copied itinerary from post",
      level: "info",
    });
    if (isBanned && !(post.viewerState?.hasCopied && post.viewerState?.copiedTripId)) return;
    trackEvent("post_copy_clicked", { post_id: post.id, already_copied: !!post.viewerState?.hasCopied });
    const token = getAuthToken();
    if (!token) {
      toast.error('Please log in to copy this itinerary.');
      return;
    }

    // Already have a copy of this one — go straight to it. No confirm dialog
    // and no API call: nothing new is being created, so "Copy this trip?"
    // would be asking about something that already happened.
    if (post.viewerState?.hasCopied && post.viewerState?.copiedTripId) {
      navigate(`/itinerary/${post.viewerState.copiedTripId}`);
      return;
    }

    if (!window.confirm('Copy this trip to your dashboard? You can customize dates and details before booking.')) {
      return;
    }

    setIsCopying(true);
    try {
      const response = await copyPost(post.id);
      // The backend hands back the existing copy instead of erroring if one
      // already exists (e.g. viewerState was stale) — the toast should say so
      // rather than claiming a fresh copy that didn't happen.
      //
      // `creditsAwarded` is only present when the backend actually wrote a
      // ledger row for this copy, so the credit line cannot appear on a repeat
      // copy, a self-copy, or when the reward rule is switched off. The
      // frontend never decides whether credits were earned — it reports what
      // the transaction did.
      if (response.alreadyCopied) {
        toast.success("You've already copied this itinerary — opening it.", { icon: '✨' });
      } else if (response.creditsAwarded) {
        toast.success(
          `Itinerary copied successfully. You earned ${response.creditsAwarded.amount} RoamPoints!`,
          { icon: '🎉' },
        );
      } else {
        toast.success('Itinerary copied successfully!', { icon: '✨' });
      }
      trackEvent("post_copied", {
        post_id: post.id,
        already_copied: !!response.alreadyCopied,
        credits_awarded: !!response.creditsAwarded,
      });
      // The copy endpoint returns { trip: { id, destination, status } } — there is
      // no top-level tripId, so this navigated to /itinerary/undefined and the page
      // then 404'd on GET /api/itinerary/trips/undefined.
      navigate(`/itinerary/${response.trip.id}`);
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "community");
        scope.setTag("app", "web");
        scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
        Sentry.captureException(new CommunityError(err instanceof Error ? err.message : String(err)));
      });
      if (err.status === 429) {
        toast.error("You've hit the daily copy limit. Try again tomorrow.");
      } else if (err.status === 404) {
        toast.error("This trip isn't available anymore.");
      } else {
        toast.error('Failed to copy itinerary. Please try again.');
      }
    } finally {
      setIsCopying(false);
    }
  };

  // For the About card: a photo that is not the cover, and the places as tags.
  const aboutPhotoIndex = media.findIndex((m) => m !== coverPhoto && (m.kind || 'image') === 'image' && (m.cardUrl || m.publicUrl || m.fullUrl));
  const aboutPhoto = aboutPhotoIndex >= 0 ? media[aboutPhotoIndex] : null;
  // The stops in the trip's own route ("Nainital → Udaipur → Mumbai"), then the kind of trip — so a tag is always something the page already says.
  const placeTags = [
    ...new Set(
      [...String(trip?.destination || '').split(/→|->|,/), trip?.tripType]
        .map((t) => String(t || '').trim().split(' ').join(''))
        .filter(Boolean),
    ),
  ].slice(0, 8);

  const handleDeletePost = async () => {
    Sentry.addBreadcrumb({
      category: "user-action",
      message: "Deleted post",
      level: "info",
    });
    // `post.reviewId` was never set on the normalized post, so this called
    // deleteReview(undefined) and hit /api/reviews/undefined — a 404 every time.
    const reviewId = post.review?.id;
    if (!reviewId) {
      toast.error("Couldn't find the review for this post.");
      setShowDeleteConfirm(false);
      return;
    }

    setIsDeleting(true);
    try {
      await deleteReview(reviewId);
      trackEvent("post_deleted", { post_id: post.id, review_id: reviewId });
      toast.success("Post deleted successfully.");
      navigate("/community"); // Navigate away on success
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "community");
        scope.setTag("app", "web");
        scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
        Sentry.captureException(new CommunityError(err instanceof Error ? err.message : String(err)));
      });
      toast.error(err.message || "Failed to delete post.");
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    /* overflow-x-clip (not overflow-x-hidden) is the backstop, not the fix:
       user-supplied text is broken with `break-words` where it is rendered.
       Without this, one unbreakable string widened the whole document and
       every section rendered off-screen — which reads as the page being
       zoomed in. `hidden` was the original choice, but any overflow-x value
       other than `visible`/`clip` forces the browser to compute overflow-y as
       `auto` on this element too (per the CSS Overflow spec's paired-axis
       rule), which turns it into position:sticky's nearest scroll ancestor
       instead of the real page — that's what silently broke the sidebar's
       `sticky top-24` below. `clip` is exempt from that rule, so it clips the
       same runaway text without hijacking sticky positioning anywhere on the
       page. */
    <div id="itinerary-pdf-content" className="min-h-screen bg-gray-50 pb-20 overflow-x-clip">
      {/* Print styles live in src/index.css, scoped to @media print + this id. */}

      {/* Preview Banner */}
      {isPreview && (
        <div className="bg-amber-500 text-white text-center py-2 font-medium sticky top-0 z-50 print-hide">
          Preview Mode: You are viewing your {status} post.
        </div>
      )}

      {/* Hero. Taller than the content needs so the cover photo has presence, but
          still capped — at the original 80vh the trip facts and the itinerary
          both sat below the fold on a laptop. */}
      <section className="relative h-[440px] w-full bg-gray-900 sm:h-[460px]">
        <img 
          src={coverPhoto?.fullUrl || coverPhoto?.publicUrl || `https://image.pollinations.ai/prompt/${encodeURIComponent(trip.destination + ' ultra high quality 8k landscape photography masterpiece')}?width=1920&height=1080&nologo=true`} 
          alt={trip.destination} 
          className="w-full h-full object-cover"
        />
        {/* Bottom-weighted scrim. Explicit stops rather than Tailwind's evenly
            spaced from/via/to: an even ramp put ~45% black across the middle of
            the frame, dimming the photo's subject. This keeps the top two-thirds
            of the image clear and only darkens the band the text actually sits in. */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.72) 14%, rgba(0,0,0,0.38) 30%, rgba(0,0,0,0.10) 48%, rgba(0,0,0,0) 65%)",
          }}
        />
        {/* Just enough at the very top to keep the back control legible. */}
        <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/30 to-transparent pointer-events-none" />

        {/* Back out of the post. `top-24` clears the fixed site navbar (h-16
            lg:h-20) — anything higher is rendered underneath it and unclickable,
            which is exactly what happened to the old share/report buttons. */}
        <div className={`absolute top-24 lg:top-28 left-0 right-0 ${PAGE_CONTAINER} print-hide`}>
          <div className="flex items-center gap-2">
            {/* Mobile: one icon-only button, back to Community. Desktop: the
                original pair — a labeled Dashboard button plus a separate
                Community link — since there's room for both. */}
            <button
              type="button"
              onClick={() => navigate('/community')}
              aria-label="Back to community"
              className="sm:hidden inline-flex items-center p-2.5 rounded-lg bg-black/30 backdrop-blur-md ring-1 ring-white/20 text-white hover:bg-black/50 transition-colors cursor-pointer"
            >
              <ArrowLeft size={15} />
            </button>

            <Link
              to="/community"
              className="hidden sm:inline-flex items-center gap-2 pl-2.5 pr-3.5 py-2 rounded-xl bg-black/35 backdrop-blur-md ring-1 ring-white/25 text-white text-[13px] font-medium hover:bg-black/55 transition-colors"
            >
              <ArrowLeft size={15} /> Back to Community
            </Link>
          </div>
        </div>
        
        {/* Share / report / delete used to float at top-6 — directly underneath the
            fixed site navbar, which is 64-80px tall, so they were invisible and
            unclickable on every screen. They now sit with the other hero actions. */}

        {/* Hero Content */}
        <div className={`absolute bottom-16 left-0 right-0 ${PAGE_CONTAINER}`}>
          <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-5">
            {/* min-w-0 lets this column shrink below its content width — without
                it a flex child refuses to shrink and pushes the row wider. */}
            <div className="flex-1 min-w-0">
              {/* One quiet eyebrow line rather than two competing pills. */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-4 text-white/75 [text-shadow:0_1px_10px_rgba(0,0,0,0.6)]">
                <span className="text-[10px] font-semibold uppercase tracking-[0.2em]">
                  {trip.tripType || "Solo"}
                </span>
                <span className="w-1 h-1 rounded-full bg-white/40" />
                <span className="text-[13px] font-medium flex items-center gap-1.5 min-w-0">
                  <MapPin size={13} className="text-white/50 shrink-0" />
                  <span className="truncate">{trip.destination}</span>
                </span>
              </div>
              
              {/* The itinerary's own title. Using the review body here meant a
                  rambling — or, in test data, a single unbroken 40-character —
                  paragraph became the page's H1, which `line-clamp` cannot wrap,
                  so it ran off the edge and widened the whole document.
                  `break-words` guards the fallback against the same thing.
                  The shadow, rather than a heavier scrim, keeps the photo bright. */}
              <h1 className="text-[24px] md:text-[38px] font-semibold text-white leading-[1.15] tracking-[-0.02em] max-w-3xl line-clamp-2 break-words [text-shadow:0_2px_16px_rgba(0,0,0,0.55)]">
                {trip?.itinerary?.tripSummary?.tripTitle?.trim() || `${trip.destination} Itinerary`}
              </h1>

              {/* The traveller's own words still lead the page — as a quote under
                  the title, where length is not a layout risk. */}
              {review?.description?.trim() && (
                <p className="mt-2.5 text-white/70 text-[14px] leading-relaxed max-w-2xl line-clamp-1 break-words [text-shadow:0_1px_10px_rgba(0,0,0,0.6)]">
                  “{review.description.trim()}”
                </p>
              )}

              <div className="flex items-center gap-4 mt-5 text-gray-200 [text-shadow:0_1px_10px_rgba(0,0,0,0.6)]">
                <Link to={`/u/${user.handle || user.name}`} className="flex items-center gap-3 group">
                  <div className="w-9 h-9 rounded-full bg-white/10 backdrop-blur-sm ring-1 ring-white/25 group-hover:ring-white/50 transition-all flex items-center justify-center text-white font-semibold text-sm">
                    {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-white/90 text-[14px] font-medium group-hover:text-white transition-colors">{user.name}</span>
                    {user.isVerified && <CheckCircle size={14} className="text-blue-400" fill="white" />}
                    {user.badges && user.badges.length > 0 && (
                      <div className="ml-2 scale-90 origin-left brightness-125">
                        <BadgeStrip 
                          badges={user.badges.filter(b => !(user.isVerified && b.code === 'VERIFIED_TRAVELER')).slice(0, 2)} 
                          isVerified={user.isVerified} 
                          compact={true} 
                        />
                      </div>
                    )}
                  </div>
                </Link>
                
                {review?.overallRating > 0 && (
                  <>
                    <div className="w-px h-4 bg-white/20 rounded-full" />
                    {/* Five stars read as a rating at a glance; a bare "5" next to a
                        single star was ambiguous. No invented 4.9 fallback. */}
                    <div className="flex items-center gap-1.5">
                      <span className="flex">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star
                            key={n}
                            size={13}
                            className={n <= review.overallRating ? "text-amber-400" : "text-white/25"}
                            fill="currentColor"
                          />
                        ))}
                      </span>
                      <span className="text-white/90 text-[13px] font-medium">
                        {review.overallRating}.0
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
            
          </div>
        </div>
      </section>

      {/* Trip facts. One panel divided into columns rather than four separate
          cards with heavy drop shadows — the facts belong together, and the row of
          floating tiles was competing with the hero for attention. */}
      <div className={`${PAGE_CONTAINER} -mt-10 relative z-10`}>
        <div className="flex flex-col gap-5 rounded-2xl border border-gray-200/70 bg-white p-4 shadow-[0_18px_50px_-18px_rgba(15,23,42,0.3)] sm:p-5 lg:flex-row lg:items-center">
          <div className="grid flex-1 grid-cols-2 gap-x-4 gap-y-4 md:grid-cols-4">
            {[
              { Icon: Calendar, label: "Dates", value: `${startDate} – ${endDate}`, tone: "bg-teal-50 text-teal-600" },
              { Icon: Clock, label: "Duration", value: `${tripDays} days · ${tripDays - 1} nights`, tone: "bg-rose-50 text-rose-500" },
              { Icon: Wallet, label: budgetInfo.label, value: budgetInfo.value, tone: "bg-amber-50 text-amber-500" },
              { Icon: Users, label: "Travellers", value: travellerLabel, tone: "bg-blue-50 text-blue-500" },
            ].map((item) => (
              <div key={item.label} className="flex min-w-0 items-center gap-3">
                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${item.tone}`} aria-hidden="true">
                  <item.Icon size={20} strokeWidth={2} />
                </span>
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">{item.label}</div>
                  <div className="truncate text-[14px] font-semibold tracking-[-0.01em] text-gray-900">{item.value}</div>
                </div>
              </div>
            ))}
          </div>

          {/* The two things to do with the trip, with the facts rather than over the photo. */}
          <div className="flex shrink-0 items-center gap-3 border-t border-gray-100 pt-4 print-hide lg:border-l lg:border-t-0 lg:pl-5 lg:pt-0">
            <div className="rounded-xl border border-gray-200 bg-white px-1 shadow-sm">
              <LikeButton post={post} />
            </div>
            {post.status === 'approved' && (
              <button
                onClick={handleCopy}
                disabled={isCopying || (isBanned && !post.viewerState?.hasCopied)}
                title={post.viewerState?.hasCopied ? "You've already copied this itinerary" : undefined}
                className={`flex items-center gap-2 whitespace-nowrap rounded-xl bg-teal-600 px-5 py-3 text-sm font-semibold tracking-[-0.01em] text-white shadow-md transition-colors hover:bg-teal-700 ${isCopying || (isBanned && !post.viewerState?.hasCopied) ? 'cursor-not-allowed opacity-70' : ''}`}
              >
                {post.viewerState?.hasCopied ? <Check size={16} /> : <Copy size={16} />}
                {isCopying ? "Copying…" : post.viewerState?.hasCopied ? "View My Copy" : "Copy Itinerary"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Section tabs. These jump to a section rather than swapping the page's
          contents: the discussion stays at the foot of the story where it reads as
          a response to it, and nothing is hidden from someone scrolling straight
          down (or printing the page). */}
      <div className={`${PAGE_CONTAINER} mt-10 print-hide`}>
        {/* Scrolls horizontally rather than wrapping — a second row of tabs would
            read as a different control. */}
        <nav className="flex gap-8 sm:gap-10 border-b border-gray-200 overflow-x-auto scrollbar-hidden">
          {[
            { key: 'overview', label: 'Overview', Icon: MapPin, target: 'overview' },
            // Each tab is offered only when its section has something in it.
            ...(trip?.itinerary?.dayWiseItinerary?.length > 0
              ? [{ key: 'daywise', label: 'Day wise', Icon: Calendar, target: 'daily-plan' }]
              : []),
            ...(hasHotelSection
              ? [{ key: 'hotels', label: 'Hotels', Icon: Building2, target: 'hotels' }]
              : []),
            ...(hasBudgetSection
              ? [{ key: 'budget', label: 'Budget', Icon: Wallet, target: 'budget' }]
              : []),
            {
              key: 'comments',
              label: 'Comments',
              Icon: MessageCircle,
              count: counters?.commentCount ?? 0,
              target: 'discussion',
            },
          ].map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => {
                  trackEvent("post_section_tab_clicked", { post_id: post.id, tab: tab.key });
                  setActiveTab(tab.key);
                  document
                    .getElementById(tab.target)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className={`group flex items-center gap-2 pb-3 -mb-px border-b-2 shrink-0 whitespace-nowrap transition-colors cursor-pointer ${
                  isActive
                    ? 'border-teal-600 text-gray-900'
                    : 'border-transparent text-gray-400 hover:text-gray-700'
                }`}
              >
                <tab.Icon
                  size={15}
                  className={isActive ? 'text-teal-600' : 'text-gray-300 group-hover:text-gray-400'}
                />
                <span className="text-sm font-semibold tracking-[-0.01em]">{tab.label}</span>
                {tab.count > 0 && (
                  <span className="text-[11px] font-semibold text-gray-400 tabular-nums">
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      <div className={`${PAGE_CONTAINER} py-10 flex flex-col lg:flex-row gap-10`}>

        {/* Main Content (Left). No max-width cap: with one it stopped at 896px and
            left a dead gutter between the story and the sidebar on wide screens. */}
        <div className="flex-1 min-w-0">

          {/* Review Section */}
          <section id="overview" className="mb-16 scroll-mt-24">
            <div className="mb-10 rounded-2xl border border-gray-200/70 bg-white p-5 shadow-sm sm:p-7">
              <div className="flex flex-col gap-6 md:flex-row md:items-start">
                <div className="min-w-0 flex-1">
                  <h2 className="mb-4 flex items-center gap-3 text-[24px] font-bold tracking-[-0.02em] text-gray-900">
                    <span className="h-7 w-1.5 rounded-full bg-teal-600" aria-hidden="true" />
                    About This Journey
                  </h2>
                  {/* `review.text` was replaced by `review.description` in Round 12. The previous
                      fallback here was a hardcoded paragraph of sample copy, which rendered as if
                      it were the traveller's own words whenever the field was empty. */}
                  {review?.description && (
                    <div className="text-[15px] leading-relaxed text-gray-600 whitespace-pre-wrap break-words">
                      {review.description}
                    </div>
                  )}
                  {placeTags.length > 0 && (
                    <div className="mt-5 flex flex-wrap gap-2">
                      {placeTags.map((tag) => (
                        <span key={tag} className="rounded-full bg-teal-50 px-3 py-1 text-[12px] font-semibold text-teal-700">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {aboutPhoto && (
                  <button
                    type="button"
                    onClick={() => openLightbox(aboutPhotoIndex)}
                    aria-label="View photo"
                    className="shrink-0 cursor-pointer overflow-hidden rounded-xl md:w-64"
                  >
                    <img
                      src={aboutPhoto.cardUrl || aboutPhoto.publicUrl || aboutPhoto.fullUrl}
                      alt={trip.destination}
                      className="h-44 w-full object-cover transition-transform duration-500 hover:scale-105 md:h-48"
                    />
                  </button>
                )}
              </div>
            </div>

            {/* Highlights and what fell short, side by side as in the design. The
                second card is the traveller's `improvements` answer — labelling it
                "Hidden Gems" would put words in their mouth that they never wrote. */}
            {(review?.bestExperience?.trim() || review?.improvements?.trim()) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12 items-stretch">
                {review?.bestExperience?.trim() && (
                  <div className="flex flex-col overflow-hidden rounded-2xl border border-gray-200/70 bg-white shadow-sm">
                    <div className="h-1 bg-teal-500" aria-hidden="true" />
                    <div className="flex flex-1 flex-col p-5 sm:p-6">
                      <div className="mb-4 flex items-center gap-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-600" aria-hidden="true">
                          <CheckCircle size={20} />
                        </span>
                        <h3 className="text-[17px] font-bold text-gray-900">Key Highlights</h3>
                      </div>
                      <ul className="space-y-3 text-[14px] leading-relaxed text-gray-700">
                        {review.bestExperience.split('\n').filter(line => line.trim()).map((line, idx) => (
                          <li key={idx} className="flex min-w-0 gap-3">
                            <span className="mt-[3px] flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-teal-50 text-teal-600" aria-hidden="true">
                              <Check size={11} strokeWidth={3} />
                            </span>
                            <span className="min-w-0 break-words">{line}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
                {review?.improvements?.trim() && (
                  <div className="flex flex-col overflow-hidden rounded-2xl border border-gray-200/70 bg-white shadow-sm">
                    <div className="h-1 bg-amber-400" aria-hidden="true" />
                    <div className="flex flex-1 flex-col p-5 sm:p-6">
                      <div className="mb-4 flex items-center gap-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-500" aria-hidden="true">
                          <Lightbulb size={20} />
                        </span>
                        <h3 className="text-[17px] font-bold text-gray-900">Good to know</h3>
                      </div>
                      <p className="whitespace-pre-wrap break-words text-[14px] leading-relaxed text-gray-700">
                        {review.improvements}
                      </p>
                      <a
                        href="#journey-map"
                        className="mt-auto inline-flex w-fit items-center gap-1.5 pt-5 text-sm font-semibold text-teal-700 transition-colors hover:text-teal-800"
                      >
                        <MapPin size={15} aria-hidden="true" /> View location on map <ChevronRight size={15} aria-hidden="true" />
                      </a>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Journey Map. Sits at the end of the story in the design — after the
                highlights, as the visual summary of everything above. */}
            {(trip?.itinerary?.tripSummary?.citiesBreakdown || trip?.destination) && (
              <div id="journey-map" className="mb-12 order-last">
                <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Your Journey</h3>
                <ItineraryMap
                  citiesBreakdown={trip.itinerary.tripSummary.citiesBreakdown}
                  startingFrom={trip.itinerary.tripSummary.startingFrom || trip.startingLocation}
                  destination={trip.destination}
                  destinationData={trip.destinations?.[0] || trip.destinationData} 
                  travelModes={trip.travelBetween || trip.travelToDest}
                  isMultiDest={Array.isArray(trip.destinations) && trip.destinations.length >= 2}
                  userDestinations={trip.destinations}
                  startingLocationData={trip.startingLocationData}
                />
              </div>
            )}

            {/* How the traveller rated it. One panel: the headline score, then the
                categories as bars. Four identical donuts in four unrelated colours
                said nothing the numbers didn't — and being equal-sized, they made
                a 5.0 and a 2.0 look equally weighted at a glance. Bars compare. */}
            {review && (
              review.overallRating || review.hotelRating || review.foodRating || review.transportRating ||
              review.valueForMoney || review.itinerarySatisfaction || review.itineraryEasyToFollow ||
              review.aiHelpfulness || review.wouldRecommend != null
            ) && (
              <div className="mb-12">
                <h2 className="text-[26px] font-semibold tracking-[-0.02em] text-gray-900 mb-6">
                  Travel Metrics
                </h2>

                <div className="bg-white rounded-2xl border border-gray-200/70 shadow-sm p-6 sm:p-7 flex flex-col sm:flex-row gap-7">
                  {/* Headline score */}
                  {review.overallRating > 0 && (
                    <div className="flex sm:flex-col items-center sm:items-start gap-4 sm:gap-2 sm:pr-7 sm:border-r border-gray-100 shrink-0">
                      <span className="text-5xl font-semibold text-gray-900 leading-none tracking-[-0.03em]">
                        {review.overallRating.toFixed(1)}
                      </span>
                      <div>
                        <span className="flex mb-1">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <Star
                              key={n}
                              size={14}
                              className={n <= review.overallRating ? "text-amber-400" : "text-gray-200"}
                              fill="currentColor"
                            />
                          ))}
                        </span>
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-[0.14em] whitespace-nowrap">
                          Overall rating
                        </p>
                      </div>
                      {review.wouldRecommend != null && (
                        <span className={`hidden sm:inline-flex items-center gap-1.5 mt-1 text-xs font-semibold ${review.wouldRecommend ? "text-teal-600" : "text-rose-500"}`}>
                          {review.wouldRecommend ? <ThumbsUp size={13} /> : <ThumbsDown size={13} />}
                          {review.wouldRecommend ? "Would recommend" : "Wouldn't recommend"}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Category breakdown */}
                  <ul className="flex-1 min-w-0 grid sm:grid-cols-2 gap-x-8 gap-y-3.5 self-center w-full">
                    {[
                      { key: "hotelRating", label: "Stay", Icon: Building2 },
                      { key: "foodRating", label: "Food", Icon: Utensils },
                      { key: "transportRating", label: "Transport", Icon: Plane },
                      { key: "itinerarySatisfaction", label: "Itinerary", Icon: Map },
                      { key: "valueForMoney", label: "Value for money", Icon: DollarSign },
                      { key: "itineraryEasyToFollow", label: "Easy to follow", Icon: CheckCircle },
                      { key: "aiHelpfulness", label: "Romaya's help", Icon: Sparkles },
                    ]
                      .filter((row) => review[row.key] > 0)
                      .map((row) => {
                        const value = review[row.key];
                        return (
                          <li key={row.key} className="flex items-center gap-3 min-w-0">
                            <row.Icon size={14} className="text-gray-400 shrink-0" />
                            <span className="text-sm text-gray-600 w-32 shrink-0">{row.label}</span>
                            <span className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden min-w-[3rem]">
                              <span
                                className="block h-full bg-teal-500 rounded-full transition-all duration-500"
                                style={{ width: `${(value / 5) * 100}%` }}
                              />
                            </span>
                            <span className="text-sm font-semibold text-gray-900 tabular-nums w-7 text-right shrink-0">
                              {value.toFixed(1)}
                            </span>
                          </li>
                        );
                      })}
                    {review.wouldRecommend != null && (
                      <li className="flex items-center gap-3 min-w-0 sm:hidden">
                        {review.wouldRecommend ? (
                          <ThumbsUp size={14} className="text-teal-500 shrink-0" />
                        ) : (
                          <ThumbsDown size={14} className="text-rose-400 shrink-0" />
                        )}
                        <span className="text-sm text-gray-600">
                          {review.wouldRecommend ? "Would recommend" : "Wouldn't recommend"}
                        </span>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            )}
          </section>

          {/* Media Gallery */}
          {media && media.length > 0 && (
            <section className="mb-16">
              <h2 className="text-[26px] font-semibold tracking-[-0.02em] text-gray-900 mb-8">Journey Gallery</h2>
              {/* 'travel' is the only category the backend ever assigns a video
                  (VIDEO_CATEGORIES in romaya-api/src/config/storage.js) — this
                  list previously said 'travel_video', a category that no media
                  row can ever have, so every uploaded video silently vanished
                  from the gallery regardless of its own render branch below. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8">
                {['cover', 'destination', 'hotel', 'food', 'attraction', 'travel', 'other'].map(cat => {
                  const catMedia = media.filter(m => (m.category || 'other') === cat);
                  if (catMedia.length === 0) return null;
                  return (
                    <div key={cat}>
                      <h3 className="text-xl font-bold text-gray-800 mb-4 capitalize">{cat.replace('_', ' ')}</h3>
                      <div className="columns-2 gap-4 space-y-4">
                        {catMedia.map((item) => {
                          const globalIndex = media.findIndex(m => m.id === item.id);
                          return (
                            <div
                              key={item.id}
                              className="rounded-3xl overflow-hidden cursor-pointer relative group break-inside-avoid"
                              onClick={() => openLightbox(globalIndex)}
                            >
                              {item.kind === 'video' ? (
                                <div className="w-full aspect-video bg-black relative">
                                  <video src={item.publicUrl} className="w-full h-full object-cover opacity-70" />
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-12 h-12 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center text-white pl-1">
                                      ▶
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <img
                                  src={item.thumbUrl || item.publicUrl}
                                  alt="Gallery item"
                                  className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
                                  loading="lazy"
                                />
                              )}
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Trip Budget. Every figure is the itinerary's own costing — nothing is
              recomputed here, so what a traveller copying this trip sees matches
              what the planner produced. */}
          {hasBudgetSection && (
            <section id="budget" className="mb-16 scroll-mt-24">
              <h2 className="mb-1.5 flex items-center gap-3 text-[24px] font-bold tracking-[-0.02em] text-gray-900">
                <span className="h-7 w-1.5 rounded-full bg-teal-600" aria-hidden="true" />
                Trip Budget
              </h2>
              <p className="mb-6 pl-[18px] text-sm text-gray-500">
                Estimated for this itinerary — actual spend varies with season and
                booking choices.
              </p>

              <div className="overflow-hidden rounded-2xl border border-gray-200/70 bg-white shadow-sm">
                {/* Headline totals */}
                <div className="grid grid-cols-1 divide-y divide-gray-100 sm:grid-cols-2 sm:divide-x sm:divide-y-0">
                  <div className="flex items-center gap-4 p-5 sm:p-6">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-500" aria-hidden="true">
                      <Wallet size={22} strokeWidth={2} />
                    </span>
                    <div className="min-w-0">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                        Estimated total
                      </span>
                      <p className="text-[28px] font-bold leading-tight tracking-[-0.02em] text-gray-900">
                        {costSummary?.estimatedTotal || budgetInfo.value}
                      </p>
                      <p className="text-xs text-gray-500">
                        for {travellerCount} {travellerCount === 1 ? "traveller" : "travellers"} ·{" "}
                        {tripDays} days
                      </p>
                    </div>
                  </div>

                  {costSummary?.perPerson && (
                    <div className="flex items-center gap-4 p-5 sm:p-6">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-500" aria-hidden="true">
                        <Users size={22} strokeWidth={2} />
                      </span>
                      <div className="min-w-0">
                        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                          Per person
                        </span>
                        <p className="text-[28px] font-bold leading-tight tracking-[-0.02em] text-gray-900">
                          {costSummary.perPerson}
                        </p>
                        <p className="text-xs text-gray-500">
                          roughly {tripDays > 0 ? Math.round(toAmount(costSummary.perPerson) / tripDays).toLocaleString() : 0} per day
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Where the money goes: the split as one bar, then each part with its own colour. */}
                {costRows.length > 0 && (
                  <div className="border-t border-gray-100 p-5 sm:p-6">
                    <p className="mb-4 text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                      Where it goes
                    </p>

                    <div className="mb-6 flex h-3 w-full overflow-hidden rounded-full bg-gray-100" role="img" aria-label="Share of the budget by category">
                      {costRows.map((row, i) => (
                        <span
                          key={row.key}
                          className={BUDGET_TONES[i % BUDGET_TONES.length].bar}
                          style={{ width: `${costTotal > 0 ? (row.amount / costTotal) * 100 : 0}%` }}
                          title={row.label}
                        />
                      ))}
                    </div>

                    <ul className="space-y-5">
                      {costRows.map((row, i) => {
                        const share = costTotal > 0 ? Math.round((row.amount / costTotal) * 100) : 0;
                        const tone = BUDGET_TONES[i % BUDGET_TONES.length];
                        return (
                          <li key={row.key}>
                            <div className="mb-2 flex items-center gap-3">
                              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${tone.tile}`} aria-hidden="true">
                                <row.Icon size={17} />
                              </span>
                              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-800">
                                {row.label}
                              </span>
                              <span className="text-sm font-bold tabular-nums text-gray-900">
                                {row.display}
                              </span>
                              <span className={`w-11 rounded-full px-2 py-0.5 text-center text-xs font-semibold tabular-nums ${tone.pill}`}>
                                {share}%
                              </span>
                            </div>
                            <span className="ml-12 block h-1.5 overflow-hidden rounded-full bg-gray-100">
                              <span
                                className={`block h-full rounded-full transition-all duration-500 ${tone.bar}`}
                                style={{ width: `${share}%` }}
                              />
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Hotel Recommendations */}
          <div id="hotels" className="mb-16 scroll-mt-24">
            <HotelInformationSection 
              tripSummary={trip?.itinerary?.tripSummary}
              tripData={trip}
              dayWiseItinerary={trip?.itinerary?.dayWiseItinerary}
              isExpanded={hotelsExpanded}
              onToggleExpand={() => setHotelsExpanded(!hotelsExpanded)}
            />
          </div>

          {/* Itinerary Timeline. DayWiseItinerarySection renders its own "Day-wise
              Itinerary" heading with working Expand All / Collapse All buttons —
              the same card the dashboard uses bare (Itinerary.jsx). This used to
              wrap it in a second, redundant "Day-by-Day Story" header with a dead
              Expand All button (no onClick), which is why this page looked more
              cluttered on mobile than the dashboard's version of the same section. */}
          {trip?.itinerary?.dayWiseItinerary?.length > 0 && (
            <div id="daily-plan" className="mb-16 scroll-mt-24">
               <DayWiseItinerarySection
                 dayWiseItinerary={trip.itinerary.dayWiseItinerary}
                 expandedDays={expandedDays}
                 setExpandedDays={setExpandedDays}
                 toggleDay={toggleDay}
                 isEditable={false}
                 variant="showcase"
                 tripSummary={trip.itinerary.tripSummary}
               />
            </div>
          )}
          
          {/* Community Discussion — last on the page, as in the design: it reads
              as a response to the story above it. */}
          <section id="discussion" className="mb-10 print-hide scroll-mt-24">
             <CommentList postId={post.id} initialCount={counters?.commentCount ?? 0} />
          </section>

        </div>

        {/* Sidebar (Right) */}
        {/* On a phone the author card comes first, above the story; from lg it is the sticky column on the right. */}
        <div className="order-first lg:order-none w-full lg:w-[320px] shrink-0 print-hide">
          <div className="lg:sticky lg:top-24">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200/70 overflow-hidden">
              
              {/* The trip's own photo along the top of the card, the traveller's avatar overlapping it. */}
              <div className="relative h-24">
                <img
                  src={coverPhoto?.fullUrl || coverPhoto?.publicUrl || coverPhoto?.cardUrl || `https://image.pollinations.ai/prompt/${encodeURIComponent(trip.destination + ' landscape photography')}?width=640&height=240&nologo=true`}
                  alt=""
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" aria-hidden="true" />
              </div>

              {/* Traveler Section (Top Half) */}
              <div className="p-5 pt-0 flex flex-col items-start text-left border-b border-gray-50 bg-slate-50/50">
                <Link to={`/u/${user.handle || user.name}`} className="relative -mt-9 mb-3 block shrink-0 group">
                  <div className="w-[72px] h-[72px] rounded-full bg-teal-600 ring-4 ring-white flex items-center justify-center text-white font-semibold text-2xl">
                    {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                  </div>
                  {user.isVerified && (
                    <div className="absolute bottom-0.5 right-0.5 w-5 h-5 bg-teal-600 rounded-full border-2 border-white flex items-center justify-center">
                      <CheckCircle size={11} className="text-white" />
                    </div>
                  )}
                </Link>
                <div className="flex items-center justify-between w-full mb-4 gap-3">
                  <div className="min-w-0">
                    <Link to={`/u/${user.handle || user.name}`} className="hover:underline">
                      <h3 className="font-bold text-lg text-gray-900 truncate">{user.name}</h3>
                    </Link>
                    <p className="text-[12px] text-gray-500 truncate">Adventure Creator</p>
                  </div>
                  <FollowButton
                    handle={user.handle || user.name}
                    initialIsFollowing={post.viewerState?.following || false}
                    className="shrink-0"
                  />
                </div>

                <div className="flex justify-start items-center gap-6 text-xs font-bold text-gray-700 w-full pl-1">
                  <div className="text-left">
                    <div className="text-lg font-semibold text-gray-900 mb-0">{user.postCount ?? 0}</div>
                    <div className="text-gray-400 text-[9px] uppercase tracking-widest">Routes</div>
                  </div>
                  <div className="w-px h-8 bg-gray-200"></div>
                  <div className="text-left">
                    <div className="text-lg font-semibold text-gray-900 mb-0">{(counters?.viewCount ?? 0) >= 1000 ? ((counters?.viewCount ?? 0)/1000).toFixed(1) + 'k' : counters?.viewCount ?? 0}</div>
                    <div className="text-gray-400 text-[9px] uppercase tracking-widest">Views</div>
                  </div>
                </div>
              </div>

              {/* Engagement Section (Bottom Half) */}
              <div className="p-5">
                <h3 className="font-bold text-teal-600 text-[10px] tracking-widest uppercase mb-3 text-left">Engagement Metrics</h3>
                <div className="grid grid-cols-2 gap-2">
                  <LikeButton post={post} variant="large" />
                  <SaveButton post={post} variant="large" />
                  <div 
                    onClick={() => setShowShare(true)}
                    className="border border-gray-100 rounded-xl p-3 flex flex-col items-center justify-center gap-1.5 hover:border-rose-200 hover:bg-rose-50 transition-colors cursor-pointer text-center group"
                  >
                    <div className="text-rose-500 group-hover:scale-110 transition-transform"><Share2 size={18} /></div>
                    <div>
                      <div className="font-bold text-gray-900 text-sm mb-0">{(counters?.copyCount ?? 0).toLocaleString()}</div>
                      <div className="text-[9px] text-gray-400 uppercase tracking-widest font-bold">Shares</div>
                    </div>
                  </div>
                  <div 
                    onClick={handleDownloadPDF}
                    className="border border-gray-100 rounded-xl p-3 flex flex-col items-center justify-center gap-1.5 hover:border-teal-200 hover:bg-teal-50 transition-colors cursor-pointer text-center group print:hidden"
                  >
                    <div className="text-teal-500 group-hover:scale-110 transition-transform"><Download size={18} /></div>
                    <div>
                      <div className="font-bold text-gray-900 text-sm mb-0">Get</div>
                      <div className="text-[9px] text-gray-400 uppercase tracking-widest font-bold">PDF</div>
                    </div>
                  </div>
                </div>

                {/* Secondary actions. Kept out of the hero, and quiet by design —
                    reporting is rare and deleting is destructive, so neither
                    should compete with Copy Itinerary. */}
                <div className="mt-4 pt-4 border-t border-gray-100 flex flex-col gap-1">
                  <button
                    type="button"
                    disabled={isBanned}
                    onClick={() => {
                      if (isBanned) return;
                      trackEvent('report_initiated', { target_type: 'post', target_id: post.id });
                      setReportingPostId(post.id);
                    }}
                    className={`flex items-center gap-2 text-xs text-gray-400 hover:text-gray-600 transition-colors py-1 ${isBanned ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <Flag size={13} /> Report this itinerary
                  </button>

                  {post.viewerState?.isOwner && (
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="flex items-center gap-2 text-xs text-gray-400 hover:text-red-600 transition-colors cursor-pointer py-1"
                    >
                      <Trash2 size={13} /> Delete this post
                    </button>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>

      </div>

      {lightboxOpen && (
        <MediaLightbox 
          media={media} 
          currentIndex={lightboxIndex} 
          onClose={() => setLightboxOpen(false)} 
          onNavigate={handleLightboxNav} 
        />
      )}
      
      <ShareBottomSheet 
        post={post} 
        isOpen={showShare} 
        onClose={() => setShowShare(false)} 
      />
      
      <ReportModal
        isOpen={!!reportingPostId}
        onClose={() => setReportingPostId(null)}
        targetId={reportingPostId}
        targetType="post"
      />
      
      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4 print-hide">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Delete this post?</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete this review? Deleting this review will also unpublish the community post. This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-5 py-2.5 text-gray-700 font-medium hover:bg-gray-100 rounded-xl transition-colors"
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeletePost}
                  disabled={isDeleting}
                  className={`px-5 py-2.5 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition-colors flex items-center gap-2 ${
                    isDeleting ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  {isDeleting ? "Deleting..." : "Delete Post"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PostDetail;
